/* eslint-disable @typescript-eslint/no-empty-function */
import Button from '@carvana/ds-button';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useCallback, useMemo } from 'react';
import styles from './Datepicker.module.css';
import { Calendar, Header, MonthPicker, MonthTitle, Spacer } from './Datepicker.styles';
import { Month } from './Month';
import {
  DateRange,
  deriveDateRange,
  getMonthName,
  getPreviousDisplayYear,
  getPreviousMonth,
  getNextDisplayYear,
  getNextMonth,
  isDateAfter,
  isDateBefore,
  getWeekStartDate,
  getWeekEndDate,
  getPreviousWeek,
  getNextWeek,
  getWeekTitle,
  shouldShowYear,
  type ShowYearMode
} from './utilities';
import { createComponent, type ElementWithProps } from './utils';
import { Week } from './Week';

export interface BaseDatePickerProps {
  /** id passed to the root div */
  id?: string;
  /** className passed to the root div */
  className?: string;
  /** Indicates whether to enable date range selection. */
  rangeEnabled?: boolean;
  /** A pre-selected date. */
  selected?: Date | DateRange;
  /** Disable selecting dates before this date. */
  disableDatesBefore?: Date;
  /** Disable selecting dates after this date. */
  disableDatesAfter?: Date;
  /** Disable specific dates. */
  disableSpecificDates?: Date[];
  /** value for the data-testid attribute */
  testId?: string;
  /** Indicates whether to show the header. */
  showHeader?: boolean;
  /** The header text to display. */
  header?: string;
  /** The title alignment. */
  titleStyle?: 'default' | 'center';
  /** Indicates whether to show the left month pager arrow. */
  showLeftArrow?: boolean;
  /** Indicates whether to show the right month pager arrow. */
  showRightArrow?: boolean;
  /** Click handler for when a date is selected. */
  onChange?: (date: DateRange) => void;
}

export type MonthDatePickerProps = BaseDatePickerProps & {
  variant?: 'month';
  month: number;
  year: number;
  onMonthChange?: (month: number, year: number) => void;
};

export type WeekDatePickerProps = BaseDatePickerProps & {
  variant: 'week';
  weekDate: Date;
  showYear?: ShowYearMode;
  weekStartDay: number;
  onWeekChange: (weekStartDate: Date, weekEndDate: Date) => void;
};

export type DatePickerProps = MonthDatePickerProps | WeekDatePickerProps;

function isMonthDatePickerProps(props: DatePickerProps): props is MonthDatePickerProps {
  return props.variant === 'month' || props.variant === undefined;
}

function isWeekDatePickerProps(props: DatePickerProps): props is WeekDatePickerProps {
  return props.variant === 'week';
}

const DatepickerRoot = createComponent<
  ElementWithProps<Pick<BaseDatePickerProps, 'id' | 'className'>>
>('div', () => ({
  className: styles.dateSelect
}));

export default function Datepicker(props: DatePickerProps) {
  const {
    variant = 'month',
    rangeEnabled,
    selected,
    disableDatesBefore,
    disableDatesAfter,
    disableSpecificDates,
    onChange = () => {},
    testId = 'datePicker',
    showHeader = true,
    header = 'Select Date',
    titleStyle = 'default',
    showLeftArrow = true,
    showRightArrow = true,
    id,
    className
  } = props;

  const monthProps = isMonthDatePickerProps(props) ? props : null;
  const providedMonth = monthProps?.month;
  const providedYear = monthProps?.year;
  const onMonthChange = monthProps?.onMonthChange;

  const weekProps = isWeekDatePickerProps(props) ? props : null;
  const providedWeekDate = weekProps?.weekDate;
  const showYear = weekProps?.showYear ?? 'auto';
  const weekStartDay = weekProps?.weekStartDay ?? 0;
  const onWeekChange = weekProps?.onWeekChange;
  const today = new Date();
  const month = providedMonth ?? today.getMonth();
  const year = providedYear ?? today.getFullYear();
  const weekDate = providedWeekDate ?? today;
  const monthIsSelected = useMemo(() => deriveDateRange(selected), [selected]);

  const currentWeekStartDate = useMemo(
    () => (variant === 'week' ? getWeekStartDate(weekDate, weekStartDay) : null),
    [variant, weekDate, weekStartDay]
  );

  const currentWeekEndDate = useMemo(
    () => (variant === 'week' ? getWeekEndDate(weekDate, weekStartDay) : null),
    [variant, weekDate, weekStartDay]
  );

  const title = useMemo(() => {
    if (variant === 'week' && currentWeekStartDate && currentWeekEndDate) {
      const shouldShow = shouldShowYear(
        showYear,
        currentWeekStartDate,
        new Date(),
        currentWeekEndDate
      );
      return getWeekTitle(currentWeekStartDate, currentWeekEndDate, shouldShow);
    }
    return `${getMonthName(month)} ${year}`;
  }, [variant, currentWeekStartDate, currentWeekEndDate, showYear, month, year]);

  const firstOfPeriod = useMemo(() => {
    if (variant === 'week' && currentWeekStartDate) {
      return currentWeekStartDate;
    }
    return new Date(year, month, 1);
  }, [variant, currentWeekStartDate, year, month]);

  const lastOfPeriod = useMemo(() => {
    if (variant === 'week' && currentWeekEndDate) {
      return currentWeekEndDate;
    }
    return new Date(year, month + 1, 0);
  }, [variant, currentWeekEndDate, year, month]);

  const disablePrevious = useMemo(() => {
    if (variant === 'week') {
      if (!disableDatesBefore) {
        return false;
      }
      const previousWeekEnd = getWeekEndDate(getPreviousWeek(weekDate), weekStartDay);
      return isDateBefore(previousWeekEnd, disableDatesBefore);
    }
    return isDateBefore(firstOfPeriod, disableDatesBefore ?? firstOfPeriod);
  }, [variant, weekDate, disableDatesBefore, weekStartDay, firstOfPeriod]);

  const disableNext = useMemo(() => {
    if (variant === 'week') {
      if (!disableDatesAfter) {
        return false;
      }
      const nextWeekStart = getWeekStartDate(getNextWeek(weekDate), weekStartDay);
      return isDateAfter(nextWeekStart, disableDatesAfter);
    }
    return isDateAfter(lastOfPeriod, disableDatesAfter ?? lastOfPeriod);
  }, [variant, weekDate, disableDatesAfter, weekStartDay, lastOfPeriod]);

  const monthChangeHandler = useCallback(
    (newMonth: number, newYear: number) => {
      if (onMonthChange) {
        onMonthChange(newMonth, newYear);
      }
    },
    [onMonthChange]
  );

  const weekChangeHandler = useCallback(
    (newWeekDate: Date) => {
      if (onWeekChange) {
        const weekStart = getWeekStartDate(newWeekDate, weekStartDay);
        const weekEnd = getWeekEndDate(newWeekDate, weekStartDay);
        onWeekChange(weekStart, weekEnd);
      }
    },
    [onWeekChange, weekStartDay]
  );

  const dateSelectionHandler = useCallback(
    (range: DateRange) => {
      onChange(range);
    },
    [onChange]
  );

  const previousMonth = getPreviousMonth(month);
  const previousYear = getPreviousDisplayYear(month, year);
  const nextMonth = getNextMonth(month);
  const nextYear = getNextDisplayYear(month, year);
  const previousWeekDate = getPreviousWeek(weekDate);
  const nextWeekDate = getNextWeek(weekDate);

  const handlePreviousNavigation = useCallback(() => {
    if (variant === 'week') {
      weekChangeHandler(previousWeekDate);
    } else {
      monthChangeHandler(previousMonth, previousYear);
    }
  }, [
    variant,
    weekChangeHandler,
    previousWeekDate,
    monthChangeHandler,
    previousMonth,
    previousYear
  ]);

  const handleNextNavigation = useCallback(() => {
    if (variant === 'week') {
      weekChangeHandler(nextWeekDate);
    } else {
      monthChangeHandler(nextMonth, nextYear);
    }
  }, [variant, weekChangeHandler, nextWeekDate, monthChangeHandler, nextMonth, nextYear]);

  const renderDefaultTitle = useCallback(
    () => (
      <>
        <MonthTitle titleStyle={titleStyle}>{title}</MonthTitle>
        <Button
          variant="tertiary"
          size="xs"
          icon
          onClick={handlePreviousNavigation}
          aria-label={variant === 'week' ? 'Previous Week' : 'Previous Month'}
          aria-disabled={disablePrevious}
          disabled={disablePrevious}
          data-cv-test={variant === 'week' ? 'previous-week-button' : 'previous-month-button'}
          data-analytics-id="pp_datepicker_previous"
        >
          <ChevronLeft size={24} color="currentColor" />
        </Button>
        <Button
          variant="tertiary"
          size="xs"
          icon
          onClick={handleNextNavigation}
          aria-label={variant === 'week' ? 'Next Week' : 'Next Month'}
          aria-disabled={disableNext}
          disabled={disableNext}
          data-cv-test={variant === 'week' ? 'next-week-button' : 'next-month-button'}
          data-analytics-id="pp_datepicker_next"
        >
          <ChevronRight size={24} color="currentColor" />
        </Button>
      </>
    ),
    [
      titleStyle,
      title,
      variant,
      disablePrevious,
      disableNext,
      handlePreviousNavigation,
      handleNextNavigation
    ]
  );

  const renderCenteredTitle = useCallback(
    () => (
      <>
        {showLeftArrow ? (
          <Button
            variant="tertiary"
            size="xs"
            icon
            onClick={handlePreviousNavigation}
            aria-label={variant === 'week' ? 'Previous Week' : 'Previous Month'}
            aria-disabled={disablePrevious}
            disabled={disablePrevious}
            data-cv-test={variant === 'week' ? 'previous-week-button' : 'previous-month-button'}
            data-analytics-id="pp_datepicker_previous"
          >
            <ChevronLeft size={24} color="currentColor" />
          </Button>
        ) : (
          <Spacer />
        )}
        <MonthTitle titleStyle={titleStyle}>{title}</MonthTitle>
        {showRightArrow ? (
          <Button
            variant="tertiary"
            size="xs"
            icon
            onClick={handleNextNavigation}
            aria-label={variant === 'week' ? 'Next Week' : 'Next Month'}
            aria-disabled={disableNext}
            disabled={disableNext}
            data-cv-test={variant === 'week' ? 'next-week-button' : 'next-month-button'}
            data-analytics-id="pp_datepicker_next"
          >
            <ChevronRight size={24} color="currentColor" />
          </Button>
        ) : (
          <Spacer />
        )}
      </>
    ),
    [
      showLeftArrow,
      disablePrevious,
      titleStyle,
      title,
      showRightArrow,
      disableNext,
      variant,
      handlePreviousNavigation,
      handleNextNavigation
    ]
  );
  return (
    <DatepickerRoot data-testid={testId} data-cv-test="datePicker" id={id} className={className}>
      {showHeader && (
        <Header data-testid="datepicker-header">
          <CalendarIcon size={16} />
          {header}
        </Header>
      )}
      <MonthPicker data-testid="datepicker-month-picker">
        {titleStyle === 'default' ? renderDefaultTitle() : renderCenteredTitle()}
      </MonthPicker>
      <Calendar data-testid="datepicker-calendar">
        {variant === 'week' ? (
          <Week
            data-testid="datepicker-week"
            weekDate={weekDate}
            weekStartDay={weekStartDay}
            rangeEnabled={rangeEnabled}
            selected={monthIsSelected}
            disableDatesBefore={disableDatesBefore}
            disableDatesAfter={disableDatesAfter}
            disableSpecificDates={disableSpecificDates}
            onChange={dateSelectionHandler}
          />
        ) : (
          <Month
            data-testid="datepicker-month"
            month={month}
            year={year}
            rangeEnabled={rangeEnabled}
            selected={monthIsSelected}
            disableDatesBefore={disableDatesBefore}
            disableDatesAfter={disableDatesAfter}
            disableSpecificDates={disableSpecificDates}
            onChange={dateSelectionHandler}
          />
        )}
      </Calendar>
    </DatepickerRoot>
  );
}
